import { useRef, useCallback, useState } from 'react';
import * as Cesium from 'cesium';

// Classification legend classes (matching ColorLegend.jsx)
const CLASSIFICATION_CLASSES = [
  { value: 1, color: 'gray' },
  { value: 2, color: 'brown' },
  { value: 3, color: 'greenyellow' },
  { value: 4, color: 'green' },
  { value: 5, color: 'darkgreen' },
  { value: 6, color: 'red' },
  { value: 7, color: 'orange' },
];
const CLASSIFICATION_LEGEND_VALUES = CLASSIFICATION_CLASSES.map(c => c.value);

const CLASSIFICATION_ALL_CONDITIONS = [
  ["${Classification} === 1", "color('gray')"],
  ["${Classification} === 2", "color('brown')"],
  ["${Classification} === 3", "color('greenyellow')"],
  ["${Classification} === 4", "color('green')"],
  ["${Classification} === 5", "color('darkgreen')"],
  ["${Classification} === 6", "color('red')"],
  ["${Classification} === 7", "color('orange')"],
  ["${Classification} === 8", "color('cyan')"],
  ["${Classification} === 9", "color('blue')"],
  ["${Classification} === 10", "color('yellow')"],
  ["${Classification} === 11", "color('purple')"],
  ["${Classification} === 12", "color('pink')"],
  ["${Classification} === 13", "color('lightblue')"],
  ["${Classification} === 14", "color('lightgreen')"],
  ["${Classification} === 15", "color('gold')"],
  ["${Classification} === 16", "color('magenta')"],
  ["${Classification} === 17", "color('lime')"],
  ["${Classification} === 18", "color('navy')"],
  ["true", "color('white')"],
];

const buildClassificationStyle = (enabledValues) => {
  const hiddenValues = CLASSIFICATION_LEGEND_VALUES.filter(v => !enabledValues.includes(v));
  const showExpr = hiddenValues.length === 0
    ? 'true'
    : hiddenValues.map(v => `\${Classification} !== ${v}`).join(' && ');
  return new Cesium.Cesium3DTileStyle({
    show: showExpr,
    color: { conditions: CLASSIFICATION_ALL_CONDITIONS },
  });
};

function getStableElevationRange(tileset) {
  // Stable "up" at tileset center in world/ECEF
  const refCenterWC = tileset.boundingSphere.center.clone();
  const upWC = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(refCenterWC, new Cesium.Cartesian3());

  const bv = tileset.root.boundingVolume;
  let corners = [];

  if (bv._orientedBoundingBox || bv instanceof Cesium.OrientedBoundingBox) {
    const obb = bv._orientedBoundingBox || bv;
    const c = obb.center;
    const ha = obb.halfAxes;
    const ax0 = new Cesium.Cartesian3(ha[0], ha[1], ha[2]);
    const ax1 = new Cesium.Cartesian3(ha[3], ha[4], ha[5]);
    const ax2 = new Cesium.Cartesian3(ha[6], ha[7], ha[8]);
    for (let i = 0; i < 8; i++) {
      const x = (i & 1) ? 1 : -1;
      const y = (i & 2) ? 1 : -1;
      const z = (i & 4) ? 1 : -1;
      const corner = Cesium.Cartesian3.clone(c);
      Cesium.Cartesian3.add(corner, Cesium.Cartesian3.multiplyByScalar(ax0, x, new Cesium.Cartesian3()), corner);
      Cesium.Cartesian3.add(corner, Cesium.Cartesian3.multiplyByScalar(ax1, y, new Cesium.Cartesian3()), corner);
      Cesium.Cartesian3.add(corner, Cesium.Cartesian3.multiplyByScalar(ax2, z, new Cesium.Cartesian3()), corner);
      corners.push(corner);
    }
  } else if (bv instanceof Cesium.BoundingSphere) {
    const c = bv.center, r = bv.radius;
    corners = [
      Cesium.Cartesian3.add(c, Cesium.Cartesian3.multiplyByScalar(upWC, r, new Cesium.Cartesian3()), new Cesium.Cartesian3()),
      Cesium.Cartesian3.add(c, Cesium.Cartesian3.multiplyByScalar(upWC, -r, new Cesium.Cartesian3()), new Cesium.Cartesian3()),
    ];
  } else {
    const c = tileset.boundingSphere.center, r = tileset.boundingSphere.radius;
    corners = [
      Cesium.Cartesian3.add(c, Cesium.Cartesian3.multiplyByScalar(upWC, r, new Cesium.Cartesian3()), new Cesium.Cartesian3()),
      Cesium.Cartesian3.add(c, Cesium.Cartesian3.multiplyByScalar(upWC, -r, new Cesium.Cartesian3()), new Cesium.Cartesian3()),
    ];
  }

  let minH = Number.POSITIVE_INFINITY;
  let maxH = -Number.POSITIVE_INFINITY;
  for (const p of corners) {
    const h = Cesium.Cartesian3.dot(Cesium.Cartesian3.subtract(p, refCenterWC, new Cesium.Cartesian3()), upWC);
    minH = Math.min(minH, h);
    maxH = Math.max(maxH, h);
  }
  return { min: minH, max: maxH, refCenterWC, upWC };
}

// GLSL shader source for property visualization
const VERTEX_SHADER_TEXT = `
    void vertexMain(VertexInput vsInput, inout czm_modelVertexOutput vsOutput) {
      vsOutput.pointSize = u_pointSize;
    }
  `;

const FRAGMENT_SHADER_TEXT = `
    // Turbo colormap polynomial approximation
    vec3 TurboColormap(float x) {
      const vec4 kRedVec4 = vec4(0.13572138, 4.61539260, -42.66032258, 132.13108234);
      const vec4 kGreenVec4 = vec4(0.09140261, 2.19418839, 4.84296658, -14.18503333);
      const vec4 kBlueVec4 = vec4(0.10667330, 12.64194608, -60.58204836, 110.36276771);
      const vec2 kRedVec2 = vec2(-152.94239396, 59.28637943);
      const vec2 kGreenVec2 = vec2(4.27729857, 2.82956604);
      const vec2 kBlueVec2 = vec2(-89.90310912, 27.34824973);
      x = clamp(x,0.0,1.0);
      vec4 v4 = vec4(1.0, x, x * x, x * x * x);
      vec2 v2 = v4.zw * v4.z;
      return vec3(
        dot(v4, kRedVec4)   + dot(v2, kRedVec2),
        dot(v4, kGreenVec4) + dot(v2, kGreenVec2),
        dot(v4, kBlueVec4)  + dot(v2, kBlueVec2)
      );
    }

    // Viridis colormap (degree-6 polynomial, IQ/iquilezles.org)
    vec3 ViridisColormap(float t) {
      const vec3 c0=vec3(0.2777273272234177,0.005407344544966578,0.3340998053353061);
      const vec3 c1=vec3(0.1050930431085774,1.404613529898575,1.384590162594685);
      const vec3 c2=vec3(-0.3308618287255563,0.214847559468213,0.09509516302823659);
      const vec3 c3=vec3(-4.634230498983486,-5.799100973351585,-19.33244095627987);
      const vec3 c4=vec3(6.228269936347081,14.17993336680509,56.69055260068105);
      const vec3 c5=vec3(4.776384997670288,-13.74514537774601,-65.35303263337234);
      const vec3 c6=vec3(-5.435455855934631,4.645852612178535,26.3124352495832);
      return clamp(c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6))))),0.0,1.0);
    }

    // Magma colormap (degree-6 polynomial, IQ)
    vec3 MagmaColormap(float t) {
      const vec3 c0=vec3(-0.002136485053939582,-0.000749655052795221,-0.005386127855323933);
      const vec3 c1=vec3(0.2516605407371642,0.6775232436837668,2.494026599312351);
      const vec3 c2=vec3(8.353717279216625,-3.577719514958484,0.3144679030132573);
      const vec3 c3=vec3(-27.66873308576866,14.26473078096533,-13.64921318813922);
      const vec3 c4=vec3(52.17613981234068,-27.94360607168351,12.94416944419548);
      const vec3 c5=vec3(-50.76852536473588,29.04658282127291,4.23415299384598);
      const vec3 c6=vec3(18.65570506591883,-11.48977351997711,-5.601961508734096);
      return clamp(c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6))))),0.0,1.0);
    }

    // Inferno colormap (degree-6 polynomial, IQ)
    vec3 InfernoColormap(float t) {
      const vec3 c0=vec3(0.0002189403691192265,0.001651004010226525,-0.01948089843709184);
      const vec3 c1=vec3(0.1065134194856116,0.5639564367884091,3.932712388889277);
      const vec3 c2=vec3(11.60249308247187,-3.972853965665698,-15.9423941062914);
      const vec3 c3=vec3(-41.70399613139459,17.43639888205313,44.35414519872813);
      const vec3 c4=vec3(77.162935699427,-33.40235894210092,-81.80730925738993);
      const vec3 c5=vec3(-71.31942824499214,32.62606426397723,73.20951985803202);
      const vec3 c6=vec3(25.13112622477341,-12.24266895238567,-23.07032500287172);
      return clamp(c0+t*(c1+t*(c2+t*(c3+t*(c4+t*(c5+t*c6))))),0.0,1.0);
    }

    // Cividis colormap (8-stop LUT)
    vec3 CividisColormap(float t) {
      t = clamp(t, 0.0, 1.0);
      float x = t * 7.0;
      int i = int(x);
      float f = fract(x);
      vec3 c0, c1;
      if      (i==0){c0=vec3(0.000,0.126,0.302);c1=vec3(0.061,0.184,0.406);}
      else if (i==1){c0=vec3(0.061,0.184,0.406);c1=vec3(0.148,0.253,0.490);}
      else if (i==2){c0=vec3(0.148,0.253,0.490);c1=vec3(0.264,0.330,0.548);}
      else if (i==3){c0=vec3(0.264,0.330,0.548);c1=vec3(0.422,0.418,0.560);}
      else if (i==4){c0=vec3(0.422,0.418,0.560);c1=vec3(0.590,0.517,0.522);}
      else if (i==5){c0=vec3(0.590,0.517,0.522);c1=vec3(0.758,0.626,0.443);}
      else          {c0=vec3(0.758,0.626,0.443);c1=vec3(0.996,0.977,0.478);}
      return mix(c0, c1, f);
    }

    // CET-L1: perceptually uniform greyscale
    vec3 CetL1Colormap(float t) {
      return vec3(clamp(t, 0.0, 1.0));
    }

    // Colormap dispatcher
    vec3 ApplyColormap(float t, int mode) {
      if (mode == 1) return ViridisColormap(t);
      else if (mode == 2) return MagmaColormap(t);
      else if (mode == 3) return InfernoColormap(t);
      else if (mode == 4) return CividisColormap(t);
      else if (mode == 5) return CetL1Colormap(t);
      else return TurboColormap(t);
    }

    void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
      // Round points
      float d = length(gl_PointCoord - vec2(0.5));
      if (d > 0.5) { discard; }

      // Default to source RGB (prevents gray when no mode is selected)
      vec3 col = fsInput.attributes.color.rgb;

      // INTENSITY
      if (u_useIntensity && u_hasIntensity) {
        float intensity = fsInput.attributes.intensity;
        float ni = (intensity - u_intensityRange.x) / (u_intensityRange.y - u_intensityRange.x);
        col = ApplyColormap(clamp(ni, 0.0, 1.0), u_intensityColormap);
        material.diffuse = col;
      }
      // ELEVATION (stable height along world "up")
      else if (u_useElevation) {
        vec3 posWC = fsInput.attributes.positionWC;
        float h = dot(posWC - u_refCenterWC, u_upWC);
        float t = (h - u_elevationRange.x) / (u_elevationRange.y - u_elevationRange.x);
        col = ApplyColormap(clamp(t, 0.0, 1.0), u_elevationColormap);
        material.diffuse = col;
      }
      // DISTANCE TO CAMERA
      else if (u_useDistance) {
        vec3 posWC = fsInput.attributes.positionWC;
        float dist = length(posWC - czm_viewerPositionWC);
        float td = (dist - u_distanceRange.x) / (u_distanceRange.y - u_distanceRange.x);
        col = ApplyColormap(1.0 - clamp(td, 0.0, 1.0), u_distanceColormap);
        material.diffuse = col;
      }

      // Profiler highlighting
      if (u_profilerEnabled) {
        vec3 worldPos = fsInput.attributes.positionWC;
        vec3 boxCenter = u_profilerCenter;
        vec3 halfDims = u_profilerDimensions * 0.5;

        vec3 relativePos = worldPos - boxCenter;
        vec3 localPos = transpose(u_profilerOrientation) * relativePos;

        vec3 absLocalPos = abs(localPos);
        bool insideBox = all(lessThanEqual(absLocalPos, halfDims));

        if (insideBox) {
          if(u_profilerSelection){
            col = mix(col, vec3(1.0, 1.0, 0.0), 0.5); // Yellow highlight
          }else{
            col = mix(col, vec3(0.0, 1.0, 1.0), 0.5); // Cyan highlight
          }
          material.emissive = vec3(0.0, 0.2, 0.2);
          material.diffuse = col;
        }
      }

      //material.diffuse = col;
      material.alpha = u_pointOpacity;
    }
  `;

/**
 * Hook for managing property visualization state and shader uniforms.
 * Handles intensity, elevation, distance, classification coloring and colormaps.
 * @param {React.RefObject} tilesetRef - Reference to the point cloud tileset
 */
export const usePropertyVisualization = (tilesetRef) => {
  // Property visualization state
  const [selectedProperty, setSelectedProperty] = useState('color');
  const [intensityRange, setIntensityRange] = useState([0, 255]);
  const [elevationRange, setElevationRange] = useState([0, 1000]);
  const [elevationBounds, setElevationBounds] = useState([0, 1000]);
  const [distanceRange, setDistanceRange] = useState([0, 100]);
  const [distanceBounds, setDistanceBounds] = useState([0, 100]);
  const [classificationFilter, setClassificationFilter] = useState(CLASSIFICATION_LEGEND_VALUES);

  // Refs mirroring state for use in callbacks without stale closures
  const selectedPropertyRef = useRef('color');
  const intensityRangeRef = useRef([0, 255]);
  const elevationRangeRef = useRef([0, 1000]);
  const distanceRangeRef = useRef([0, 100]);
  const classificationFilterRef = useRef(CLASSIFICATION_LEGEND_VALUES);

  // Colormap selection state (index matches shader ApplyColormap dispatcher)
  const [intensityColormap, setIntensityColormap] = useState(5); // CET-L1 = greyscale
  const [elevationColormap, setElevationColormap] = useState(0); // Turbo
  const [distanceColormap, setDistanceColormap] = useState(0);   // Turbo
  const intensityColormapRef = useRef(5);
  const elevationColormapRef = useRef(0);
  const distanceColormapRef = useRef(0);

  // Base offset for elevation (absolute min in shader space) and span for stability
  const elevationBaseMinRef = useRef(0);
  const elevationSpanRef = useRef(1);

  /**
   * Updates the shader uniforms for property visualization
   */
  const updateShaderUniforms = useCallback((tileset, property, intensityRange, elevationRange, distanceRange) => {
    if (!tileset?.customShader) return;

    try {
      tileset.customShader.setUniform('u_useIntensity', property === 'intensity');
      tileset.customShader.setUniform('u_useElevation', property === 'elevation');
      tileset.customShader.setUniform('u_useDistance', property === 'distance');

      const [minIntensity, maxIntensity] = intensityRange;
      tileset.customShader.setUniform('u_intensityRange', new Cesium.Cartesian2(minIntensity, maxIntensity));

      // Convert slider domain [0..span] to absolute [baseMin..baseMin+span]
      const [sliderMin, sliderMax] = elevationRange;
      const baseMin = elevationBaseMinRef.current || 0;
      const span = elevationSpanRef.current || 1;
      let minElevation = baseMin + sliderMin;
      let maxElevation = baseMin + sliderMax;
      if (maxElevation - minElevation === 0) {
        maxElevation = minElevation + Math.max(1e-6, span * 1e-6);
      }
      tileset.customShader.setUniform('u_elevationRange', new Cesium.Cartesian2(minElevation, maxElevation));

      if (distanceRange && Array.isArray(distanceRange)) {
        const [minD, maxD] = distanceRange;
        const minDist = Number.isFinite(minD) ? minD : 0.0;
        const maxDist = Number.isFinite(maxD) && maxD > minDist ? maxD : Math.max(minDist + 1.0, minDist + 1.0);
        tileset.customShader.setUniform('u_distanceRange', new Cesium.Cartesian2(minDist, maxDist));
      }
    } catch (error) {
      console.warn('Failed to update shader uniforms:', error);
    }
  }, []);

  /**
   * Updates the selected property and applies either shader uniforms or Cesium3DTileStyle
   */
  const updateSelectedProperty = useCallback((property) => {
    setSelectedProperty(property);
    selectedPropertyRef.current = property;
    const tileset = tilesetRef.current;
    if (!tileset) return;

    if (['classification', 'returnNumbers', 'numberOfReturns'].includes(property)) {
      tileset.style = undefined;
      updateShaderUniforms(tileset, 'color', intensityRangeRef.current, elevationRangeRef.current);

      if (property === 'classification') {
        tileset.style = buildClassificationStyle(classificationFilterRef.current);
      } else if (property === 'returnNumbers') {
        tileset.style = new Cesium.Cesium3DTileStyle({
          color: {
            conditions: [
              ["${ReturnNumber} === 1", "color('red')"],
              ["${ReturnNumber} === 2", "color('orange')"],
              ["${ReturnNumber} === 3", "color('yellow')"],
              ["${ReturnNumber} >= 4", "color('green')"],
              ["true", "color('white')"]
            ]
          }
        });
      } else if (property === 'numberOfReturns') {
        tileset.style = new Cesium.Cesium3DTileStyle({
          color: {
            conditions: [
              ["${NumberOfReturns} === 1", "color('red')"],
              ["${NumberOfReturns} === 2", "color('orange')"],
              ["${NumberOfReturns} === 3", "color('yellow')"],
              ["${NumberOfReturns} >= 4", "color('green')"],
              ["true", "color('white')"]
            ]
          }
        });
      }
    } else {
      tileset.style = undefined;
      updateShaderUniforms(tileset, property, intensityRangeRef.current, elevationRangeRef.current, distanceRangeRef.current);
    }
  }, [tilesetRef, updateShaderUniforms]);

  const updateClassificationFilter = useCallback((enabledValues) => {
    setClassificationFilter(enabledValues);
    classificationFilterRef.current = enabledValues;
    const tileset = tilesetRef.current;
    if (!tileset || selectedPropertyRef.current !== 'classification') return;
    tileset.style = buildClassificationStyle(enabledValues);
  }, [tilesetRef]);

  const updateIntensityRange = useCallback((range) => {
    setIntensityRange(range);
    intensityRangeRef.current = range;
    const tileset = tilesetRef.current;
    if (tileset) {
      updateShaderUniforms(tileset, selectedPropertyRef.current, range, elevationRangeRef.current, distanceRangeRef.current);
    }
  }, [tilesetRef, updateShaderUniforms]);

  const updateElevationRange = useCallback((range) => {
    setElevationRange(range);
    elevationRangeRef.current = range;
    const tileset = tilesetRef.current;
    if (tileset) {
      updateShaderUniforms(tileset, selectedPropertyRef.current, intensityRangeRef.current, range, distanceRangeRef.current);
    }
  }, [tilesetRef, updateShaderUniforms]);

  const recalibrateElevation = useCallback(() => {
    const tileset = tilesetRef.current;
    if (!tileset?.customShader) return;

    const { min, max, refCenterWC, upWC } = getStableElevationRange(tileset);
    const span = Math.max(max - min, 1e-6);

    elevationBaseMinRef.current = min;
    elevationSpanRef.current = span;

    const fullRange = [0, span];
    setElevationRange(fullRange);
    elevationRangeRef.current = fullRange;
    setElevationBounds(fullRange);

    tileset.customShader.setUniform('u_refCenterWC', refCenterWC);
    tileset.customShader.setUniform('u_upWC', upWC);
    updateShaderUniforms(tileset, selectedPropertyRef.current, intensityRangeRef.current, fullRange, distanceRangeRef.current);
  }, [tilesetRef, updateShaderUniforms]);

  const updateDistanceRange = useCallback((range) => {
    setDistanceRange(range);
    distanceRangeRef.current = range;
    const tileset = tilesetRef.current;
    if (tileset) {
      updateShaderUniforms(tileset, selectedPropertyRef.current, intensityRangeRef.current, elevationRangeRef.current, range);
    }
  }, [tilesetRef, updateShaderUniforms]);

  const updateIntensityColormap = useCallback((index) => {
    setIntensityColormap(index);
    intensityColormapRef.current = index;
    if (tilesetRef.current?.customShader)
      tilesetRef.current.customShader.setUniform('u_intensityColormap', index);
  }, [tilesetRef]);

  const updateElevationColormap = useCallback((index) => {
    setElevationColormap(index);
    elevationColormapRef.current = index;
    if (tilesetRef.current?.customShader)
      tilesetRef.current.customShader.setUniform('u_elevationColormap', index);
  }, [tilesetRef]);

  const updateDistanceColormap = useCallback((index) => {
    setDistanceColormap(index);
    distanceColormapRef.current = index;
    if (tilesetRef.current?.customShader)
      tilesetRef.current.customShader.setUniform('u_distanceColormap', index);
  }, [tilesetRef]);

  /**
   * Computes elevation and distance ranges from a tileset's bounding volume
   * and updates state accordingly. Called during tileset loading.
   * @returns {{ min, max, refCenterWC, upWC }} elevation info for shader init
   */
  const initializeTilesetProperties = useCallback((tileset) => {
    const { min, max, refCenterWC, upWC } = getStableElevationRange(tileset);
    const span = max - min;

    setElevationRange([0, span]);
    elevationRangeRef.current = [0, span];
    elevationBaseMinRef.current = min;
    elevationSpanRef.current = Math.max(span, 1e-6);
    setElevationBounds([0, span]);

    const radius = tileset.boundingSphere?.radius || span || 100.0;
    const defaultMaxDist = Math.max(radius * 3.0, 1.0);
    setDistanceRange([0, defaultMaxDist]);
    distanceRangeRef.current = [0, defaultMaxDist];
    setDistanceBounds([0, defaultMaxDist]);

    return { min, max, refCenterWC, upWC };
  }, []);

  /**
   * Creates a CustomShader configured for property visualization.
   * Reads current ref values for initial uniform state.
   * @param {number} pointSize - Initial point size
   * @param {{ min, max, refCenterWC, upWC }} elevInfo - Elevation info from initializeTilesetProperties
   * @returns {Cesium.CustomShader}
   */
  const buildCustomShader = useCallback((pointSize, elevInfo) => {
    const { min, max, refCenterWC, upWC } = elevInfo;
    return new Cesium.CustomShader({
      uniforms: {
        u_pointSize: { type: Cesium.UniformType.FLOAT, value: pointSize },
        u_pointOpacity: { type: Cesium.UniformType.FLOAT, value: 1.0 },

        // Profiler uniforms
        u_profilerEnabled: { type: Cesium.UniformType.BOOL, value: false },
        u_profilerSelection: { type: Cesium.UniformType.BOOL, value: false },
        u_profilerCenter: { type: Cesium.UniformType.VEC3, value: new Cesium.Cartesian3(0, 0, 0) },
        u_profilerDimensions: { type: Cesium.UniformType.VEC3, value: new Cesium.Cartesian3(0, 0, 0) },
        u_profilerOrientation: { type: Cesium.UniformType.MAT3, value: new Cesium.Matrix3() },

        // Property visualization uniforms
        u_useIntensity: { type: Cesium.UniformType.BOOL, value: selectedPropertyRef.current === 'intensity' },
        u_useElevation: { type: Cesium.UniformType.BOOL, value: selectedPropertyRef.current === 'elevation' },
        u_hasIntensity: { type: Cesium.UniformType.BOOL, value: true },
        u_intensityRange: { type: Cesium.UniformType.VEC2, value: new Cesium.Cartesian2(intensityRangeRef.current[0], intensityRangeRef.current[1]) },
        u_elevationRange: { type: Cesium.UniformType.VEC2, value: new Cesium.Cartesian2(min, max) },
        u_useDistance: { type: Cesium.UniformType.BOOL, value: selectedPropertyRef.current === 'distance' },
        u_distanceRange: { type: Cesium.UniformType.VEC2, value: new Cesium.Cartesian2(distanceRangeRef.current[0], distanceRangeRef.current[1]) },

        // Elevation frame (stable height axis)
        u_refCenterWC: { type: Cesium.UniformType.VEC3, value: refCenterWC },
        u_upWC: { type: Cesium.UniformType.VEC3, value: upWC },

        // Colormap selection (0=Turbo,1=Viridis,2=Magma,3=Inferno,4=Cividis,5=CET-L1)
        u_intensityColormap: { type: Cesium.UniformType.INT, value: intensityColormapRef.current },
        u_elevationColormap: { type: Cesium.UniformType.INT, value: elevationColormapRef.current },
        u_distanceColormap:  { type: Cesium.UniformType.INT, value: distanceColormapRef.current },
      },
      vertexShaderText: VERTEX_SHADER_TEXT,
      fragmentShaderText: FRAGMENT_SHADER_TEXT,
    });
  }, []);

  return {
    selectedProperty,
    classificationFilter,
    intensityRange,
    elevationRange,
    distanceRange,
    distanceBounds,
    elevationBounds,
    intensityColormap,
    elevationColormap,
    distanceColormap,
    updateSelectedProperty,
    updateClassificationFilter,
    updateIntensityRange,
    updateElevationRange,
    recalibrateElevation,
    updateDistanceRange,
    updateIntensityColormap,
    updateElevationColormap,
    updateDistanceColormap,
    // Used by the orchestrator during tileset loading
    buildCustomShader,
    initializeTilesetProperties,
  };
};
